import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import Loaders from "@stadium/ui/src/components/loaders/loaders";
import Modal from "@stadium/ui/src/components/ModalNew";
import {
  CloseButton,
  ModalBody,
  ModalContent,
  ModalDialog,
  ModalFooter,
  ModalHeader,
} from "@stadium/ui/src/components/ModalNew/utils";

import { showSuccessToast } from "@stadium/services/react-toastify";

import { getUsCountryId, selectAllCountries } from "@stadium/redux-utils/selectors/configSelector";

import { getDisplayWarehouseAddress } from "@selectors/store";

import { InternalButton } from "@sharedComponents/buttons";
import { StoreContext } from "@components/store/provider";

import { useConfig } from "@stadium/hooks/lib/use-config";

import { formatTimer, getRemainingOrderExpiryTime } from "@stadium/helpers/order-utility";

import { updateOrderAddress } from "apis/order";

import CheckoutShippingAddress from "@stadium/checkout/src/components/shipping-address";
import AddressConfirmation from "@stadium/checkout/src/components/shipping-address/address-confirmation";
import useGetUserWarehouseAddress from "@stadium/hooks/lib/get-user-warehouse-address";

import styles from "./main.module.scss";

const TimerMessage = ({ orderCompletedAt }) => {
  const [timer, setTimer] = useState(getRemainingOrderExpiryTime(orderCompletedAt));

  useEffect(() => {
    const timerId = setInterval(() => {
      setTimer((timer) => timer - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [orderCompletedAt]);

  return (
    <div className={styles.info}>
      You have <span className={styles.time}>{formatTimer(timer)}</span> left to edit your order.
    </div>
  );
};
const EditOrderModal = ({
  closeModal,
  orderCountryId,
  orderAddressId,
  openCancelOrderModal,
  orderCompletedAt,
  orderNumber,
  postEditOrderSuccess,
}) => {
  const { isGlobalCountry } = useContext(StoreContext);
  const displayWarehouseAddress = useSelector(getDisplayWarehouseAddress);
  const usCountryId = useConfig(getUsCountryId);
  const [selectedAddress, setSelectedAddress] = useState({ externalId: orderAddressId });
  const showWarehouseAddress =
    displayWarehouseAddress && (orderCountryId === usCountryId || isGlobalCountry);
  const allCountries = useConfig(selectAllCountries);

  const selectedCountry = allCountries.find(({ id }) => id === orderCountryId);
  const [isLoading, setIsLoading] = useState(false);

  const { warehouseDetails, loading: warehouseAddressLoading } = useGetUserWarehouseAddress({
    skip: !showWarehouseAddress,
  });

  const onAddressUpdate = () => {
    if (!selectedAddress.externalId) return;
    setIsLoading(true);
    updateOrderAddress({
      number: orderNumber,
      shipAddressId: selectedAddress.externalId,
    })
      .then(() => {
        showSuccessToast({ message: "Your shipping address has been updated!", title: "Success!" });
        postEditOrderSuccess(orderNumber);
      })
      .finally(() => {
        closeModal();
        setIsLoading(false);
      });
  };
  const renderAddressFormUI = ({
    renderForm,
    hideAddressForm,
    addressNotVerified,
    notVerifiedAddressModalProps,
  }) => {
    if (addressNotVerified)
      return (
        <div className={styles.addressConfirmationModal}>
          <AddressConfirmation {...notVerifiedAddressModalProps} parentClassName={true} />
        </div>
      );
    return (
      <>
        {renderHeader()}
        <ModalBody className={styles.modalBody}>{renderForm()}</ModalBody>
        <ModalFooter className={styles.modalFooter}>
          <InternalButton onClick={hideAddressForm} styleType="link" className={styles.btnBack}>
            Back
          </InternalButton>
          <InternalButton type="submit" form="address-form-component" styleType="primary">
            Save Changes
          </InternalButton>
        </ModalFooter>
      </>
    );
  };
  const renderAddresses = ({
    renderSavedAddresses,
    showAddressForm,
    hideAddressForm,
    renderAddressForm,
    renderNewAddressOption,
  }) => {
    if (showAddressForm)
      return renderAddressForm({
        renderCustomAddressForm: (props) => renderAddressFormUI({ ...props, hideAddressForm }),
      });
    return (
      <>
        {renderHeader()}
        <ModalBody className={styles.modalBody}>
          <div className={styles.addressTitle}>shipping address</div>
          <div className={styles.addressBlock}>{renderSavedAddresses()}</div>
          {renderNewAddressOption()}
        </ModalBody>
        <ModalFooter className={styles.modalFooter}>
          <InternalButton onClick={closeModal} styleType="link" className={styles.webShow}>
            cancel
          </InternalButton>
          <div className={styles.actionButtons}>
            <InternalButton
              onClick={() => {
                openCancelOrderModal();
                closeModal();
              }}
              styleType="primary-outline"
            >
              cancel&nbsp;<span className={styles.mobileShow}>my&nbsp;</span>order
            </InternalButton>
            <InternalButton
              disabled={!selectedAddress.externalId}
              isLoading={isLoading}
              onClick={onAddressUpdate}
              styleType="primary"
            >
              Save<span className={styles.webShow}> &nbsp; Changes</span>
            </InternalButton>
          </div>
        </ModalFooter>
      </>
    );
  };

  const renderShippingAddress = () => {
    if (showWarehouseAddress && warehouseAddressLoading) {
      return (
        <>
          {renderHeader()}
          <ModalBody className={styles.modalBody}>
            <Loaders.PageLoader wrapperClass="pageLoaderwrap" />
          </ModalBody>
        </>
      );
    }

    return (
      <CheckoutShippingAddress
        selectedCountry={selectedCountry}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        showWarehouseAddress={showWarehouseAddress}
        renderCustomAddressUI={renderAddresses}
        displayShowMore={false}
        warehouseDetails={warehouseDetails}
      />
    );
  };

  const renderHeader = () => (
    <ModalHeader className={styles.modalHeader}>
      <div>
        <div className={styles.title}>Edit Your Order</div>
        <TimerMessage orderCompletedAt={orderCompletedAt} />
      </div>
      <CloseButton onClick={closeModal} className={styles.iconClose} />
    </ModalHeader>
  );

  return (
    <Modal closeModal={closeModal} className={styles.editModal} mobileBottomDrawer>
      <ModalDialog className={styles.modalDialog}>
        <ModalContent className={styles.modalContent}>{renderShippingAddress()}</ModalContent>
      </ModalDialog>
    </Modal>
  );
};

export default EditOrderModal;
